import { Controller } from "@hotwired/stimulus"
import { savePlan } from "plan_storage"
import { renderIcons } from "lucide_icons"

// Revision History controller — backs the Revision section of the
// native editor. Plan data lives in localStorage under the same
// 'pcp_plan_data' key the iframe editor uses, so toggling between
// /editor (HTML) and /editor/native (ERB) shows the same data.
const STORAGE_KEY = "pcp_plan_data"

export default class extends Controller {
  static targets = ["list", "empty", "rowTemplate"]

  connect() {
    this.plan = this.loadPlan()
    if (!this.plan.meta) this.plan.meta = {}
    if (!Array.isArray(this.plan.meta.revisionHistory)) this.plan.meta.revisionHistory = []
    this.renderRows()
  }

  add() {
    this.plan.meta.revisionHistory.push({
      id: Date.now() + Math.random(),
      revision: "",
      date: new Date().toISOString().split("T")[0],
      comments: ""
    })
    this.savePlan()
    this.renderRows()
  }

  remove(event) {
    const row = event.currentTarget.closest("[data-row]")
    if (!row) return
    const id = parseFloat(row.dataset.id)
    this.plan.meta.revisionHistory = this.plan.meta.revisionHistory.filter(e => e.id !== id)
    this.savePlan()
    this.renderRows()
  }

  update(event) {
    const input = event.currentTarget
    const row = input.closest("[data-row]")
    if (!row) return
    const id = parseFloat(row.dataset.id)
    const field = input.dataset.field
    const entry = this.plan.meta.revisionHistory.find(e => e.id === id)
    if (!entry || !field) return
    entry[field] = input.value
    this.savePlan()
    // Auto-grow textareas as the user types so long comments don't
    // get clipped by the overflow-hidden + resize-none CSS the
    // partial sets. Border-aware math matches dynamic-list +
    // resource-calendar.
    if (input.tagName === "TEXTAREA") this._autoSize(input)
  }

  // Same border-aware + transition-aware scrollHeight math the
  // dynamic-list controller uses — see that controller's
  // _autoSize for the full reasoning. Floors at 60px so a
  // brand-new revision row reads as a multi-line block before
  // the user types.
  _autoSize(el) {
    if (!el || el.tagName !== "TEXTAREA") return
    const prevTransition = el.style.transition
    el.style.transition = "none"
    el.style.height = "auto"
    const borderY = (el.offsetHeight - el.clientHeight) || 2
    el.style.height = `${Math.max(el.scrollHeight + borderY, 60)}px`
    void el.offsetHeight
    el.style.transition = prevTransition
    // ResizeObserver re-runs the resize whenever the textarea's
    // width changes so the row grows to fit wrapped content
    // without needing a manual refresh.
    if (!el._dlSizer && typeof ResizeObserver !== "undefined") {
      let lastWidth = el.offsetWidth
      el._dlSizer = new ResizeObserver(() => {
        const w = el.offsetWidth
        if (w === lastWidth) return
        lastWidth = w
        this._autoSize(el)
      })
      el._dlSizer.observe(el)
    }
  }

  renderRows() {
    const list = this.listTarget
    list.querySelectorAll("[data-row]").forEach(r => r.remove())
    const entries = this.plan.meta.revisionHistory || []
    this.emptyTarget.style.display = entries.length === 0 ? "" : "none"
    const tpl = this.rowTemplateTarget.content
    // Pin the add-button reference up front. `list.querySelector("button")`
    // descends, so after row 1 is inserted it would return the row's own
    // Remove button — which isn't a direct child of `list`, and
    // `insertBefore` then throws NotFoundError.
    const addBtn = list.querySelector(":scope > button")
    entries.forEach(entry => {
      const node = tpl.cloneNode(true)
      const row = node.querySelector("[data-row]")
      row.dataset.id = entry.id
      row.querySelector('[data-field="revision"]').value = entry.revision || ""
      row.querySelector('[data-field="date"]').value     = entry.date || ""
      row.querySelector('[data-field="comments"]').value = entry.comments || ""
      if (addBtn) list.insertBefore(node, addBtn)
      else        list.appendChild(node)
    })
    // Auto-size every textarea now that rows are in the DOM —
    // scrollHeight measurements only work after the element is
    // attached. Without this saved comments would render at
    // rows="2" / default height until the user clicked into them.
    list.querySelectorAll("[data-row] textarea").forEach(t => this._autoSize(t))
    if (window.lucide && typeof window.lucide.createIcons === "function") {
      renderIcons(this.element)
    }
  }

  // ---- Drag-reorder ----
  // Mirrors the dynamic-list controller's pattern so revision rows
  // reorder with the same grip-vertical handle + drop-edge cue as the
  // rest of the editor.
  dragStart(event) {
    const row = event.currentTarget
    this._dragFromId = parseFloat(row.dataset.id)
    row.classList.add("cos-row-dragging")
    event.dataTransfer.effectAllowed = "move"
    try { event.dataTransfer.setData("text/plain", String(this._dragFromId)) } catch (_) {}
  }

  dragOver(event) {
    if (this._dragFromId == null) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    const row = event.currentTarget
    if (parseFloat(row.dataset.id) === this._dragFromId) return
    const rect = row.getBoundingClientRect()
    const before = (event.clientY - rect.top) < rect.height / 2
    this.listTarget.querySelectorAll(".cos-row-drop-before, .cos-row-drop-after")
      .forEach(r => r.classList.remove("cos-row-drop-before", "cos-row-drop-after"))
    row.classList.add(before ? "cos-row-drop-before" : "cos-row-drop-after")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("cos-row-drop-before", "cos-row-drop-after")
  }

  drop(event) {
    if (this._dragFromId == null) return
    event.preventDefault()
    const row = event.currentTarget
    const fromId = this._dragFromId
    const toId   = parseFloat(row.dataset.id)
    this._clearDragMarkers()
    if (fromId === toId) return

    const items = this.plan.meta.revisionHistory
    const fromIdx = items.findIndex(e => e.id === fromId)
    let   toIdx   = items.findIndex(e => e.id === toId)
    if (fromIdx < 0 || toIdx < 0) return

    const rect = row.getBoundingClientRect()
    const before = (event.clientY - rect.top) < rect.height / 2
    let insertAt = before ? toIdx : toIdx + 1
    if (fromIdx < insertAt) insertAt -= 1

    const [moved] = items.splice(fromIdx, 1)
    items.splice(insertAt, 0, moved)
    this.savePlan()
    this.renderRows()
  }

  dragEnd(event) {
    event.currentTarget.classList.remove("cos-row-dragging")
    this._clearDragMarkers()
    this._dragFromId = null
  }

  _clearDragMarkers() {
    this.listTarget.querySelectorAll(".cos-row-drop-before, .cos-row-drop-after, .cos-row-dragging")
      .forEach(r => r.classList.remove("cos-row-drop-before", "cos-row-drop-after", "cos-row-dragging"))
  }

  loadPlan() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }

  savePlan() {
    savePlan(this.plan)
  }
}
